'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type { AtlasAnatomyPart } from '@/data/grass-atlas-morphology'
import type { ProceduralGrassModelSpec } from './model-contract'
import { ProceduralGrassViewer } from './procedural-grass-viewer'
import styles from '../grass-atlas.module.css'
import type { GrassSimulationVisualState } from '../grass-simulation'

interface ProceduralGrassLoaderProps {
  slug: string
  activePart: AtlasAnatomyPart
  dissected: boolean
  onPartChange: (part: AtlasAnatomyPart) => void
  onUnavailable: (error: unknown, stage: string) => void
  onReady?: (durationMs: number) => void
  simulation: GrassSimulationVisualState
  simulationActive: boolean
  showNumbers: boolean
}

export function ProceduralGrassLoader({
  slug,
  activePart,
  dissected,
  onPartChange,
  onUnavailable,
  onReady,
  simulation,
  simulationActive,
  showNumbers,
}: ProceduralGrassLoaderProps) {
  const [spec, setSpec] = useState<ProceduralGrassModelSpec | null>(null)
  const loadStartedAtRef = useRef(0)

  useEffect(() => {
    let cancelled = false
    setSpec(null)
    loadStartedAtRef.current = performance.now()

    void import('./grass-model-registry')
      .then(({ getProceduralGrassModel }) => {
        if (cancelled) return
        const next = getProceduralGrassModel(slug)
        if (!next) {
          onUnavailable(new Error(`No procedural model is registered for ${slug}`), 'model-registry')
          return
        }
        setSpec(next)
      })
      .catch((error) => {
        if (!cancelled) onUnavailable(error, 'model-registry-import')
      })

    return () => {
      cancelled = true
    }
  }, [onUnavailable, slug])

  const handleReady = useCallback(() => {
    onReady?.(performance.now() - loadStartedAtRef.current)
  }, [onReady])

  if (!spec) {
    return (
      <div className={styles.threeStage} aria-label="Preparing interactive grass specimen">
        <div className={styles.threeAtmosphere} aria-hidden="true" />
        <div className={styles.threeLoading} role="status">
          <span />
          Preparing model data
        </div>
      </div>
    )
  }

  return (
    <ProceduralGrassViewer
      spec={spec}
      activePart={activePart}
      dissected={dissected}
      onPartChange={onPartChange}
      onUnavailable={onUnavailable}
      onReady={handleReady}
      simulation={simulation}
      simulationActive={simulationActive}
      showNumbers={showNumbers}
    />
  )
}
